import { getStateOptions } from '@/helpers/getStateOptions';

import { Locale, SalesRepStatus, salesRepStatusLabels } from 'types';

export interface NumericCriterionFormValue {
  enabled: boolean;
  value: string; // parsed to number at submit; '' when empty
  applicationRate: string; // parsed to int 0-100 at submit
}

export interface MultiCriterionFormValue {
  enabled: boolean;
  value: string[];
  applicationRate: string;
  applicationRates: Record<string, string>;
  // Only the state criterion uses per-item template overrides; '' means "use the program template".
  templateIds?: Record<string, string>;
}

export interface WelcomeCallStipRulesFormValues {
  templateId: string;
  age: NumericCriterionFormValue;
  state: MultiCriterionFormValue;
  salesRepStatus: MultiCriterionFormValue;
  quoteMonthlyPayment: NumericCriterionFormValue;
  preferredLanguage: MultiCriterionFormValue;
}

// templateId is a program-level field, not a rule card (it has no enabled/applicationRate).
export type RuleCardKey = Exclude<keyof WelcomeCallStipRulesFormValues, 'templateId'>;

export interface RuleCardOption {
  label: string;
  value: string;
}

export interface RuleCardConfig {
  key: RuleCardKey;
  label: string;
  kind: 'numeric' | 'multi' | 'multi-with-rates';
  prefix?: string;
  suffix?: string;
  options?: RuleCardOption[];
  conditionHelperText: string;
  // Fixed width (not min-width/stretch) sized to this attribute's own content: the floating
  // label, prefix/suffix, and realistic value all fit on one line with a margin, and no more.
  // Measured against the real Saans font + DSM padding/affix metrics, not eyeballed.
  valueWidthClassName: string;
}

// Explicit allowlist per locked scope decision — deprecated-filtering alone yields 5 values, not 3.
// Exported so WelcomeCallStipRules.tsx's payload allowlist derives from this same source instead
// of hardcoding a second copy of the 3-value business set.
export const SALES_REP_STATUS_OPTIONS: Array<{ value: SalesRepStatus; label: string }> = [
  SalesRepStatus.watchlist,
  SalesRepStatus.noNewSales,
  SalesRepStatus.suspended,
].map((value) => ({ value, label: salesRepStatusLabels[value] }));

// Locale enum values ('English'/'Spanish'), not keys — the api schema and server-core allowlist
// validate against Object.values(Locale), so keys would 400 on save.
const LOCALE_OPTIONS: RuleCardOption[] = Object.values(Locale).map((value) => ({ value, label: value }));

export const RULE_CARD_CONFIGS: RuleCardConfig[] = [
  {
    key: 'age',
    label: 'Age',
    kind: 'numeric',
    prefix: '≥',
    suffix: 'years',
    conditionHelperText: 'Matches when the primary applicant age is greater than or equal to this value.',
    // Measured floor (label + '≥' + '120' + 'years' on one line): 140px. Comfortable margin above it.
    valueWidthClassName: 'w-[200px]',
  },
  {
    key: 'state',
    label: 'State',
    kind: 'multi-with-rates',
    options: getStateOptions(),
    conditionHelperText: 'Matches when the account state is any of the selected states.',
    // Measured floor: 222px, driven by 'District of Columbia' not clipping in the dropdown menu
    // (its panel width is locked to this trigger's width). Shared width across all multi cards.
    valueWidthClassName: 'w-[360px]',
  },
  {
    key: 'salesRepStatus',
    label: 'Sales Rep Status',
    kind: 'multi-with-rates',
    options: SALES_REP_STATUS_OPTIONS,
    conditionHelperText: 'Matches when the assigned sales rep has any of the selected statuses.',
    // Measured floor: 176px ('No New Sales' in the dropdown). Shared width across all multi cards.
    valueWidthClassName: 'w-[360px]',
  },
  {
    key: 'quoteMonthlyPayment',
    label: 'Quote Monthly Payment',
    kind: 'numeric',
    prefix: '≥ $',
    conditionHelperText: 'Matches when the contracted quote monthly payment is greater than or equal to this value.',
    // Measured floor (the label itself, 22 characters at 12px, is the binding constraint): 210px.
    valueWidthClassName: 'w-[280px]',
  },
  {
    key: 'preferredLanguage',
    label: 'Preferred Language',
    kind: 'multi-with-rates',
    options: LOCALE_OPTIONS,
    conditionHelperText: 'Matches when the account preferred language is any of the selected languages.',
    // Measured floor: 192px (label; option text 'English'/'Spanish' is short and non-binding).
    // Shared width across all multi cards.
    valueWidthClassName: 'w-[360px]',
  },
];

export const DEFAULT_FORM_VALUES: WelcomeCallStipRulesFormValues = {
  templateId: '',
  age: { enabled: false, value: '', applicationRate: '100' },
  state: { enabled: false, value: [], applicationRate: '100', applicationRates: {}, templateIds: {} },
  salesRepStatus: { enabled: false, value: [], applicationRate: '100', applicationRates: {} },
  quoteMonthlyPayment: { enabled: false, value: '', applicationRate: '100' },
  preferredLanguage: { enabled: false, value: [], applicationRate: '100', applicationRates: {} },
};
